import type { FinalizeFile } from "features/secureUploads/secureUploads";

import { fileSize } from "./fileModel";

/** The server's limits: backend/app/modules/secure_uploads/uploads.py. */
export const MAX_UPLOAD_FILES = 50;
export const MAX_UPLOAD_BYTES = 250 * 1024 * 1024;
export const MAX_FILE_NAME = 255;
export const UPLOAD_LIMITS = "Up to 50 files, 250 MB each";

/** What the dialog needs of a picked file - a File in the page. */
export interface PickedFile {
  name: string;
  size: number;
  type: string;
  lastModified: number;
}

export type UploadStatus = "waiting" | "uploading" | "sent" | "failed";

export interface UploadItem<F extends PickedFile = File> {
  id: string;
  file: F;
  status: UploadStatus;
  /** 0-100 while it goes to storage. */
  percent: number;
  /** Where storage keeps it once sent - kept, so a retry sends only the rest. */
  sent: FinalizeFile | null;
  error: string | null;
}

/** Editing: files can be picked and removed. Sending: they go to storage.
 * Saving: every file is in storage and the upload is being saved. */
export type UploadPhase = "editing" | "sending" | "saving";

export interface UploadQueue<F extends PickedFile = File> {
  phase: UploadPhase;
  items: UploadItem<F>[];
  /** What the last pick left out. */
  notice: string | null;
  /** Why the last upload stopped. */
  failure: string | null;
  next: number;
}

export type UploadEvent<F extends PickedFile = File> =
  | { type: "add"; files: F[]; folders?: number }
  | { type: "remove"; id: string }
  | { type: "dismissNotice" }
  | { type: "send" }
  | { type: "start"; id: string }
  | { type: "progress"; id: string; percent: number }
  | { type: "sent"; id: string; sent: FinalizeFile }
  | { type: "failed"; id: string; error: string }
  | { type: "sendDone" }
  | { type: "saveFailed"; error: string }
  | { type: "reset" };

export function emptyQueue<F extends PickedFile = File>(): UploadQueue<F> {
  return { phase: "editing", items: [], notice: null, failure: null, next: 1 };
}

/** "a.pdf", "a.pdf and b.pdf", "a.pdf, b.pdf and 3 more". */
export function namesList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  const more = names.length - 2;
  return `${names[0]}, ${names[1]} and ${more} more`;
}

const sameFile = (a: PickedFile, b: PickedFile) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

/** Take what fits the limits and say what was left out, never silently. */
function addFiles<F extends PickedFile>(
  queue: UploadQueue<F>,
  files: F[],
  folders: number,
): UploadQueue<F> {
  const items = [...queue.items];
  let next = queue.next;
  const tooBig: string[] = [];
  const tooLong: string[] = [];
  const twice: string[] = [];
  let overCount = 0;
  for (const file of files) {
    if (file.size > MAX_UPLOAD_BYTES) tooBig.push(file.name);
    else if (file.name.length > MAX_FILE_NAME)
      tooLong.push(`${file.name.slice(0, 40)}...`);
    else if (items.some((item) => sameFile(item.file, file)))
      twice.push(file.name);
    else if (items.length >= MAX_UPLOAD_FILES) overCount += 1;
    else {
      items.push({
        id: `f${next}`,
        file,
        status: "waiting",
        percent: 0,
        sent: null,
        error: null,
      });
      next += 1;
    }
  }
  const notes: string[] = [];
  if (tooBig.length)
    notes.push(
      `${namesList(tooBig)} ${tooBig.length === 1 ? "is" : "are"} larger than 250 MB.`,
    );
  if (tooLong.length)
    notes.push(
      `${namesList(tooLong)}: file names can be at most ${MAX_FILE_NAME} characters.`,
    );
  if (twice.length)
    notes.push(
      `${namesList(twice)} ${twice.length === 1 ? "is" : "are"} already in the list.`,
    );
  if (overCount)
    notes.push(
      `One upload takes up to ${MAX_UPLOAD_FILES} files - ${overCount} ${overCount === 1 ? "was" : "were"} not added.`,
    );
  if (folders)
    notes.push("Folders cannot be uploaded - choose the files inside them.");
  return {
    ...queue,
    items,
    next,
    notice: notes.length ? notes.join(" ") : null,
  };
}

const update = <F extends PickedFile>(
  queue: UploadQueue<F>,
  id: string,
  change: Partial<UploadItem<F>>,
): UploadQueue<F> => ({
  ...queue,
  items: queue.items.map((item) =>
    item.id === id ? { ...item, ...change } : item,
  ),
});

/** The upload queue: picking files, sending them to storage, saving them. */
export function uploadQueueReducer<F extends PickedFile>(
  queue: UploadQueue<F>,
  event: UploadEvent<F>,
): UploadQueue<F> {
  switch (event.type) {
    case "add":
      if (queue.phase !== "editing") return queue;
      return addFiles(queue, event.files, event.folders ?? 0);
    case "remove":
      if (queue.phase !== "editing") return queue;
      return {
        ...queue,
        items: queue.items.filter((item) => item.id !== event.id),
        failure: null,
      };
    case "dismissNotice":
      return { ...queue, notice: null };
    case "send":
      if (queue.phase !== "editing" || queue.items.length === 0) return queue;
      return {
        ...queue,
        phase: allSent(queue.items) ? "saving" : "sending",
        failure: null,
        items: queue.items.map((item) =>
          item.status === "failed"
            ? { ...item, status: "waiting", percent: 0, error: null }
            : item,
        ),
      };
    case "start":
      return update(queue, event.id, { status: "uploading", percent: 0 });
    case "progress":
      return update(queue, event.id, { percent: event.percent });
    case "sent":
      return update(queue, event.id, {
        status: "sent",
        percent: 100,
        sent: event.sent,
        error: null,
      });
    case "failed":
      return update(queue, event.id, {
        status: "failed",
        percent: 0,
        error: event.error,
      });
    case "sendDone": {
      if (queue.phase !== "sending") return queue;
      if (allSent(queue.items)) return { ...queue, phase: "saving" };
      const failed = queue.items.filter((item) => item.status !== "sent");
      return {
        ...queue,
        phase: "editing",
        failure:
          queue.items.length === 1
            ? "The file could not be sent to storage. Upload again to retry."
            : `${failed.length} of ${queue.items.length} files could not be sent to storage. Upload again to retry them.`,
      };
    }
    case "saveFailed":
      return { ...queue, phase: "editing", failure: event.error };
    case "reset":
      return emptyQueue<F>();
    default:
      return queue;
  }
}

/** The files still to go to storage. */
export function filesToSend<F extends PickedFile>(
  items: UploadItem<F>[],
): UploadItem<F>[] {
  return items.filter((item) => item.status !== "sent");
}

export function allSent(items: { status: UploadStatus }[]): boolean {
  return items.length > 0 && items.every((item) => item.status === "sent");
}

/** What finalize saves, in the order the files were picked. */
export function finalizeFiles(
  items: { sent: FinalizeFile | null }[],
): FinalizeFile[] | null {
  const files = items.map((item) => item.sent);
  return files.every((file): file is FinalizeFile => file !== null)
    ? files
    : null;
}

/** "3 files, 2.4 MB". */
export function queueSummary(items: { file: PickedFile }[]): string {
  const total = items.reduce((sum, item) => sum + item.file.size, 0);
  const count = items.length === 1 ? "1 file" : `${items.length} files`;
  return `${count}, ${fileSize(total)}`;
}

/** What keeps the Upload button off, in the order the form reads. */
export function uploadBlocker(
  files: number,
  sharedWith: number[],
): string | null {
  if (files === 0) return "Choose at least one file.";
  if (sharedWith.length === 0)
    return files === 1
      ? "Share the file with at least one person."
      : "Share the files with at least one person.";
  return null;
}

/** The chosen people who may still be picked: checking Confidential, or moving the
 * files to a project, narrows the list, and nobody it leaves out stays chosen. */
export function keepAllowed(
  chosen: number[],
  allowed: { id: number }[],
): number[] {
  const ids = new Set(allowed.map((person) => person.id));
  return chosen.filter((id) => ids.has(id));
}

/** What to say about the people a narrower list took out of Share with. */
export function removedFromShare(names: string[]): string | null {
  if (names.length === 0) return null;
  return `${namesList(names)} ${names.length === 1 ? "was" : "were"} taken out of Share with: they would not see the files there.`;
}

/** "Individual Files", "Payroll" or "Payroll / Plans". */
export function locationLabel(
  projectName?: string | null,
  collectionName?: string | null,
): string {
  if (!projectName) return "Individual Files";
  return collectionName ? `${projectName} / ${collectionName}` : projectName;
}

/** The line under the dialog's heading: where the files go, and what the
 * uploader may still choose. */
export function destinationLine({
  files,
  projectName,
  collectionName,
  canPickProject,
  canPickCollection,
}: {
  files: number;
  projectName?: string | null;
  collectionName?: string | null;
  canPickProject: boolean;
  canPickCollection: boolean;
}): string {
  const subject = files === 1 ? "This file" : "These files";
  const where = locationLabel(projectName, collectionName);
  if (!projectName && canPickProject)
    return `${subject} will go to Individual Files unless you choose a project.`;
  if (projectName && !collectionName && canPickCollection)
    return `${subject} will go to ${where} unless you choose a collection.`;
  return `${subject} will go to ${where}.`;
}

/** Under Share with. Staff decide whether recipients are notified; a client
 * user's upload always notifies them. */
export function shareHelper(staff: boolean): string {
  const rest =
    "Recipients selected here will be notified of the upload, but the file will remain visible to all users with access.";
  return staff ? `Email notifications are optional. ${rest}` : rest;
}

export function notifyHelper(notify: boolean): string {
  return notify
    ? "Recipients get an email and an in-app notification."
    : "No email or in-app notification goes out; the files still show as new.";
}

export function projectHelper(staff: boolean, hasProjects: boolean): string {
  if (!hasProjects) return "No open projects for this client.";
  return staff
    ? "Only open projects for this client are listed."
    : "Only open projects you are on are listed.";
}

/** Staff can move a file between collections later; a client user cannot. */
export function collectionHelper(staff: boolean, files: number): string {
  if (!staff)
    return "Optional. Leave it empty to add the files to the project without a collection.";
  return files > 1
    ? "Optional. You can change this at any time once the files are in the project."
    : "Optional. You can change this at any time once the file is in the project.";
}

/** The toast after an upload: "3 files uploaded to Payroll. 2 people were emailed." */
export function uploadedMessage(
  files: number,
  place: string,
  emailsSent: number,
): string {
  const what = files === 1 ? "File" : `${files} files`;
  const emailed =
    emailsSent > 0
      ? ` ${emailsSent} ${emailsSent === 1 ? "person was" : "people were"} emailed.`
      : "";
  return `${what} uploaded to ${place}.${emailed}`;
}
